import {
  afterNextRender,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  linkedSignal,
  OnDestroy,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  MibBreakpointService,
  MibButtonTogglesComponent,
  MibButtonTogglesSegment,
  MibButtonTogglesSize,
  MibButtonDirective,
  MibIconComponent,
  MibSearchFieldComponent,
  MibTooltipDirective,
} from '@medinbox/angular';
import { debounce, form, FormField } from '@angular/forms/signals';
import { Location } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { LibraryZone } from '@shared/enums/case-library';
import { Dialog } from '@angular/cdk/dialog';
import { UploadDialog } from './upload-dialog/upload-dialog';
import { PermissionService } from '@core/permissions';
import { NewLiveButton } from '@shared/components';
import { CaseLibraryStore } from './case-library-store';
import { filter, Subject, takeUntil } from 'rxjs';
import { HeaderMiddleDirective } from '@shared/components/header-bar';
import { STORAGE_KEYS } from '@shared/constants';
import { loadPreference, loadZones, savePreference, saveZones } from '@shared/utils';
import { LibraryZones } from './library-zones/library-zones';

const LIBRARY_ZONES: readonly LibraryZone[] = [LibraryZone.Chat, LibraryZone.Library];

/** Side by side, both zones show. Alone on a phone, the case library is the one to open on. */
const DEFAULT_DESKTOP_ZONES: readonly LibraryZone[] = LIBRARY_ZONES;
const DEFAULT_MOBILE_ZONE: readonly LibraryZone[] = [LibraryZone.Library];

@Component({
  imports: [
    MibButtonDirective,
    MibIconComponent,
    HeaderMiddleDirective,
    MibButtonTogglesComponent,
    NewLiveButton,
    MibTooltipDirective,
    MibSearchFieldComponent,
    FormField,
    NgTemplateOutlet,
    LibraryZones,
  ],
  templateUrl: './case-library.html',
  host: { class: 'contents' },
})
export class CaseLibrary implements OnDestroy {
  private readonly dialog = inject(Dialog);
  protected readonly permissions = inject(PermissionService);
  protected readonly breakpointService = inject(MibBreakpointService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly injector = inject(Injector);

  protected readonly library = inject(CaseLibraryStore);
  private readonly unsubscribeAll = new Subject<void>();

  /** Edits the search kept by the library, so coming back from a case shows it again. */
  protected readonly searchForm = form(this.library.searchText, (text) => debounce(text, 300));
  protected readonly notExpanded = computed(
    () => this.breakpointService.sizeClass() !== 'expanded',
  );
  protected readonly headerControlSize = computed<MibButtonTogglesSize>(() =>
    this.notExpanded() ? 'sm' : 'md',
  );
  /** Folded into its button until opened; a page arriving with a search shows it unfolded, except on a phone. */
  protected readonly searchOpen = linkedSignal(
    () => !this.notExpanded() && untracked(() => !this.searchIsEmpty()),
  );
  /** Only an opening by the button grows the field: a page arriving with a search shows it still. */
  protected readonly searchOpenedByButton = signal(false);
  // Rendered in the header bar, still found: a query follows the views of the templates it declares.
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly zonesView = viewChild(LibraryZones);

  /** On a phone the open search takes the whole application bar. */
  protected readonly searchFillsBar = computed(() => this.searchOpen() && this.notExpanded());

  constructor() {
    this.restoreChoiceFromAddress();
    effect(() => this.writeAddress(this.library.queryParams()));
    // A navigation to `/` reuses the page and drops the query without changing `queryParams`.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unsubscribeAll),
      )
      .subscribe(() => this.writeAddress(this.library.queryParams()));
    effect(() => {
      const open = this.filtersOpen();
      if (!this.notExpanded()) {
        savePreference(STORAGE_KEYS.LIBRARY_FILTERS_OPEN, String(open));
      }
    });
  }

  // Not a navigation: its NavigationStart would make the scroll savers overwrite the saved scroll.
  private writeAddress(queryParams: Params): void {
    const tree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
    this.location.replaceState(this.router.serializeUrl(tree), '', this.location.getState());
  }

  protected readonly zoneSegments: MibButtonTogglesSegment[] = [
    { id: LibraryZone.Chat, icon: 'sparkles', label: 'MIA', tooltip: 'MIA' },
    {
      id: LibraryZone.Library,
      icon: 'library-big',
      label: 'Case library',
      tooltip: 'Case library',
    },
  ];
  /** A key per layout: a phone shows one zone, side by side any combination holds. */
  protected readonly visibleZones = signal<LibraryZone[]>(
    loadZones(STORAGE_KEYS.LIBRARY_ZONES, LIBRARY_ZONES, DEFAULT_DESKTOP_ZONES),
  );
  protected readonly mobileZone = signal<LibraryZone>(
    loadZones(STORAGE_KEYS.LIBRARY_ZONE_MOBILE, LIBRARY_ZONES, DEFAULT_MOBILE_ZONE)[0],
  );
  /** Stacked, the two zones are shown one at a time, so the group navigates between them. */
  protected readonly compact = computed(() => this.breakpointService.sizeClass() === 'compact');
  protected readonly zoneGroupValue = computed<string[]>(() => {
    if (this.compact()) {
      return [this.mobileZone()];
    }
    return this.chatOnRow()
      ? this.visibleZones()
      : this.visibleZones().filter((zone) => zone !== LibraryZone.Chat);
  });
  /** Side by side on a phone, the filters take the chat's half: the chat is off the row. */
  protected readonly chatOnRow = computed(
    () =>
      this.visibleZones().includes(LibraryZone.Chat) && !(this.notExpanded() && this.filtersOpen()),
  );

  /** The desktop aside, open or closed as the reader left it and kept across a reload. */
  protected readonly filtersOpen = linkedSignal(
    () => !this.notExpanded() && loadPreference(STORAGE_KEYS.LIBRARY_FILTERS_OPEN) === 'true',
  );

  /** The page holds nothing: the imports live in the service, so closing leaves them alone. */
  protected pickVideo(): void {
    this.dialog.open(UploadDialog);
  }

  protected openSearch(): void {
    this.searchOpenedByButton.set(true);
    this.searchOpen.set(true);
    afterNextRender(() => this.searchInput()?.nativeElement.focus(), { injector: this.injector });
  }

  /** The focus leaving the search folds it back, unless it holds a text; on a phone, always. */
  protected foldSearch(event: FocusEvent): void {
    const field = event.currentTarget as HTMLElement;
    if (field.contains(event.relatedTarget as Node | null)) {
      return;
    }
    if (this.notExpanded() || this.searchIsEmpty()) {
      this.searchOpen.set(false);
    }
  }

  // The typed text, not the debounced model: a Tab right after typing still finds text.
  private searchIsEmpty(): boolean {
    return this.searchForm().controlValue().trim() === '';
  }

  /** An address without a library choice keeps the store's; the URL effect writes it back. */
  private restoreChoiceFromAddress(): void {
    const query = this.route.snapshot.queryParamMap;
    if (this.library.hasChoice(query)) {
      this.library.restore(query);
    }
  }

  protected setVisibleZones(zones: string[]): void {
    const zonesView = this.zonesView();
    zonesView?.move();
    if (this.compact()) {
      this.mobileZone.set(zones[0] as LibraryZone);
      saveZones(STORAGE_KEYS.LIBRARY_ZONE_MOBILE, zones);
      return;
    }
    saveZones(STORAGE_KEYS.LIBRARY_ZONES, zones);
    const show = () => this.visibleZones.set(zones as LibraryZone[]);
    if (zonesView) {
      zonesView.showZones(zones as LibraryZone[], show);
    } else {
      show();
    }
    // Their half is the chat's: turning MIA back on is the only toggle left while they are open.
    if (this.notExpanded()) {
      this.filtersOpen.set(false);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }
}
